import * as React from 'react';
import {Query} from 'react-apollo';

import type Moment from 'moment';

// @ts-expect-error - imported from .js/.jsx file
import {ActivityEmptyStateIcon} from 'shells/icon/svg/activity-empty-state-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ActivityErrorStateIcon} from 'shells/icon/svg/activity-error-state-icon';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {ActivityListItem} from 'shells/list-item/activity-list-item';
import {ListView} from 'shells/list-view/list-view';
import {SidebarSpecialState} from 'shells/sidebar-special-state/sidebar-special-state';

import type {
    FullActivity as FullActivityFragment,
    SidebarCalendarActivitiesQuery as SidebarCalendarActivitiesQueryQuery,
    SidebarCalendarActivitiesQueryVariables as SidebarCalendarActivitiesQueryQueryVariables,
} from 'nutshell-graphql-types';

import {GetCalendarSync} from '../setup/calendar/get-calendar-sync';

import SIDEBAR_CALENDAR_ACTIVITIES_QUERY from './graphql/queries/sidebar-calendar-activities-query.graphql';

type OwnProps = {
    userId: string;
    // @ts-expect-error
    momentDisplayDate: Moment;
    closeSidebar: () => void;
};

type GraphQLProps = {
    isLoading: boolean;
    hasError: boolean;
    refetchActivities: () => any;
    activities: FullActivityFragment[];
    userHasCalendarSyncEnabled?: boolean;
};

type Props = OwnProps & GraphQLProps;

export class SidebarCalendarActivitiesComponent extends React.PureComponent<Props> {
    componentDidMount() {
        $(window).on(Activity.events.DidSubmitSuccessfully, () => {
            this.props.refetchActivities();
        });
    }

    componentWillUnmount() {
        $(window).off(Activity.events.DidSubmitSuccessfully);
    }

    render() {
        const shouldShowSyncCta = !this.props.userHasCalendarSyncEnabled;

        return (
            <ListView
                isLoading={this.props.isLoading}
                collection={this.props.activities}
                renderRow={this.renderActivityRow}
                loadingState={{
                    customComponent: (
                        <div style={{textAlign: 'center', paddingTop: 32}}>
                            <LoadingIcon />
                        </div>
                    ),
                }}
                specialStates={[
                    {
                        shouldRender: () => this.props.hasError,
                        component: (
                            <SidebarSpecialState
                                iconComponent={<ActivityErrorStateIcon size={70} />}
                                headerText='Whoops!'
                                subheaderText={"That didn't quite go to plan"}
                                primaryCTA={{
                                    ctaText: 'Try again',
                                    onClick: this.props.refetchActivities,
                                }}
                            />
                        ),
                    },
                    {
                        shouldRender: () => this.props.activities.length === 0,
                        component: (
                            <SidebarSpecialState
                                iconComponent={<ActivityEmptyStateIcon size={70} />}
                                headerText='No activities'
                                subheaderText='Stay on top of your relationships by scheduling and logging activities with your contacts.'
                                primaryCTA={{
                                    ctaText: 'Schedule an activity',
                                    to: {
                                        hash: '/create/activities',
                                        search: location.search,
                                        state: {modal: true},
                                    },
                                    onClick: this.props.closeSidebar,
                                }}
                                secondaryCTA={
                                    shouldShowSyncCta
                                        ? {
                                              ctaText: 'Connect your calendar',
                                              to: '/my-account/calendar',
                                              onClick: this.props.closeSidebar,
                                          }
                                        : undefined
                                }
                            />
                        ),
                    },
                ]}
            />
        );
    }

    renderActivityRow = (activity: FullActivityFragment) => {
        return (
            <ActivityListItem
                key={activity.id}
                userId={this.props.userId}
                activity={{
                    id: activity.id,
                    type: activity.type,
                    name: activity.name,
                    htmlUrl: activity.htmlUrl,
                    startTime: activity.startTime,
                    endTime: activity.endTime,
                    isLogged: activity.isLogged,
                    isAllDay: activity.isAllDay,
                    isOverdue: activity.isOverdue,
                    agenda: activity.agenda,
                    participants: activity.participants,
                    showTranscribeLoggingFlow: activity.showTranscribeLoggingFlow,
                    externalMeetingServiceType: activity.externalMeetingServiceType,
                }}
                maxParticipantsDisplayed={1}
                hasBorders={true}
                hideActionMenu={true}
                hasActions={false}
            />
        );
    };
}

class ActivityCalendarQuery extends Query<
    SidebarCalendarActivitiesQueryQuery,
    SidebarCalendarActivitiesQueryQueryVariables
> {}

export const SidebarCalendarActivities = (props: OwnProps) => (
    <GetCalendarSync>
        {({calendarSync}) => (
            <ActivityCalendarQuery
                query={SIDEBAR_CALENDAR_ACTIVITIES_QUERY}
                variables={{
                    dateMin: props.momentDisplayDate.startOf('day').format(),
                    dateMax: props.momentDisplayDate.endOf('day').format(),
                }}
            >
                {({data, loading, refetch, error}) => {
                    const myActivities = data ? data.myActivities : null;

                    return (
                        <SidebarCalendarActivitiesComponent
                            {...props}
                            isLoading={loading}
                            hasError={Boolean(error)}
                            refetchActivities={refetch}
                            activities={myActivities || []}
                            userHasCalendarSyncEnabled={Boolean(
                                calendarSync && calendarSync.isEnabled
                            )}
                        />
                    );
                }}
            </ActivityCalendarQuery>
        )}
    </GetCalendarSync>
);
